import axios from "axios";
import axiosInstance from './axiosInstance';
import config from "../config/env";
import { toast } from "react-toastify";
import { getErrorMessage } from "../utils/translations";
import profileApi from "./profileApi";

const API_URL = config.API_URL;

// Create an axios instance with default configuration
const api = axios.create({
  baseURL: API_URL,
});

// Global state for subscription modal and limits refresh function
let showSubscriptionModal = null;
let refreshLimitsGlobal = null;

// Function to set the global showSubscriptionModal function
export const setGlobalShowSubscriptionModal = (modalFunction) => {
  showSubscriptionModal = modalFunction;
};

// Function to set the global refreshLimits function
export const setGlobalRefreshLimits = (refreshFunction) => {
  refreshLimitsGlobal = refreshFunction;
};

// Add a request interceptor to append the authorization header to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors globally
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const errorMessage =
      getErrorMessage(error.response?.data?.message) || "An error occurred";
    
    if (error.response && error.response.status === 402) {
      // Try to get the subscription URL and show modal
      try {
        const url = await profileApi.getWordPressLoginUrl();
        if (showSubscriptionModal) {
          showSubscriptionModal(url);
        }
      } catch (subscriptionError) {
        // If getting subscription URL fails, still show error message
        toast.error(getErrorMessage('ERROR_LOADING_BILLING_CONTACT_SUPPORT'));
      }
      // Don't show the error toast for 402 errors
    } else {
      // Instead of showing the toast here, we'll set a flag on the error object
      error.hasBeenHandled = true;
      error.handledMessage = errorMessage;
    }
    return Promise.reject(error);
  }
);

// Add these helper functions at the top of the file
const getToken = () => localStorage.getItem("token");

const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.error || defaultMessage;
  
  switch (error.response?.status) {
    case 401:
      toast.error('Session expired. Please login again.');
      break;
    case 404:
      toast.error('Chat thread not found');
      break;
    case 429:
      toast.warning('Rate limit reached. Please wait a minute before sending more messages.');
      break;
    case 402:
      toast.error('Insufficient AI credits. Please upgrade your plan.');
      break;
    default:
      toast.error(errorMessage);
  }
};

const generalApi = {
  // Keyword related API calls
  keyword: {
    // ... existing keyword methods ...
  },

  // Category related API calls
  category: {
    // ... existing category methods ...
  },

  // Project related API calls
  project: {
    createProject: async (projectData) => {
      try {
        const response = await api.post("/projects", projectData);
        if (refreshLimitsGlobal) {
          await refreshLimitsGlobal();
        }
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    getProjects: async () => {
      try {
        const response = await api.get("/projects");
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    getProjectsLists: async () => {
      try {
        const response = await api.get("/projects");
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    updateProject: async (projectData) => {
      try {
        const response = await api.put(
          `/projects/${projectData._id}`,
          projectData
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    deleteProject: async (projectId) => {
      try {
        const response = await api.delete(`/projects/${projectId}`);
        if (refreshLimitsGlobal) {
          await refreshLimitsGlobal();
        }
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    getProjectById: async (projectId) => {
      try {
        const response = await api.get(`/projects/${projectId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching project:", error);
        throw error;
      }
    },

    addBlogPost: async (blogPostData) => {
      try {
        const response = await api.post(`/blog-posts`, blogPostData);
        if (refreshLimitsGlobal) {
          await refreshLimitsGlobal();
        }
        console.log("response:", response);
        return response.data;
      } catch (error) {
        console.error("Error adding blog post:", error);
        throw error;
      }
    },

    getBlogPost: async (blogPostId) => {
      try {
        const response = await api.get(`/blog-posts/${blogPostId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching blog post:", error);
        throw error;
      }
    },

    updateBlogPost: async (blogPostId, blogPostData) => {
      try {
        const response = await api.put(
          `/blog-posts/${blogPostId}`,
          blogPostData
        );
        return response.data;
      } catch (error) {
        console.error("Error updating blog post:", error);
        throw error;
      }
    },

    deleteMeme: async (projectId, memeId) => {
      try {
        const response = await api.delete(
          `/projects/${projectId}/memes/${memeId}`
        );
        if (refreshLimitsGlobal) {
          await refreshLimitsGlobal();
        }
        return response.data;
      } catch (error) {
        console.error("Error deleting meme:", error);
        throw error;
      }
    },

    deleteArticle: async (projectId, blogPostId) => {
      try {
        const response = await api.delete(
          `/projects/${projectId}/articles/${blogPostId}`
        );
        if (refreshLimitsGlobal) {
          await refreshLimitsGlobal();
        }
        return response.data;
      } catch (error) {
        console.error("Error deleting article:", error);
        throw error;
      }
    },

    // Add item to project
    addProjectItem: async (projectId, item) => {
      try {
        // Add validation for projectId format
        if (!projectId || typeof projectId !== 'string' || projectId.trim() === '') {
          console.error('Invalid projectId provided to addProjectItem:', projectId);
          throw new Error('Invalid project ID format');
        }
        
        // Log the API call for debugging
        console.log(`Making API call to add item to project ${projectId}:`, item);
        
        // Don't add /api prefix - API_URL already has it
        const response = await api.post(`/projects/${projectId}/items`, item);
        
        if (refreshLimitsGlobal) {
          await refreshLimitsGlobal();
        }
        
        return response.data;
      } catch (error) {
        console.error(`Error adding project item to project ${projectId}:`, error);
        // Enhanced error logging
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
        } else if (error.request) {
          console.error("No response received:", error.request);
        } else {
          console.error("Error details:", error.message);
        }
        throw error;
      }
    },
    
    // Update project item
    updateProjectItem: async (projectId, itemId, updates) => {
      const response = await api.put(`/projects/${projectId}/items/${itemId}`, updates);
      return response.data;
    },
    
    // Delete project item
    deleteProjectItem: async (projectId, itemId) => {
      const response = await api.delete(`/projects/${projectId}/items/${itemId}`);
      return response.data;
    },
  },

  city: {
    // ... existing city methods ...
  },

  // Keyword analysis
  getKeywordAnalysis: async (keyword, country, city, language, searchEngine, typeParams, keywordId = null) => {
    // ... existing getKeywordAnalysis method ...
  },

  bulkKeywordAnalysis: async (keywords, country, city, language, searchEngine, projectId, typeParams) => {
    // ... existing bulkKeywordAnalysis method ...
  },

  aiOverview: {
    // ... existing aiOverview methods ...
  },

  notifications: {
    // ... existing notifications methods ...
  },

  rankTracking: {
    // ... existing rankTracking methods ...
  },

  memes: {
    // ... existing memes methods ...
  },

  prompts: {
    // ... existing prompts methods ...
  },

  images: {
    generateImage: async (data) => {
      try {
        const response = await axiosInstance.post(`/images/generate`, data);
        if (refreshLimitsGlobal) {
          await refreshLimitsGlobal();
        }
        return response.data;
      } catch (error) {
        if (error.response?.status === 402) {
          const url = await profileApi.getWordPressLoginUrl();
          showSubscriptionModal(url);
        } else if (error.response?.status === 403) {
          const url = await profileApi.getWordPressLoginUrl();
          showSubscriptionModal(url);
        }
        throw error;
      }
    },
    uploadImage: async (formData) => {
      try {
        const response = await axiosInstance.post('/images/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    }
  },

  facebookAds: {
    // ... existing facebookAds methods ...
  },

  // Chat Endpoints
  chat: {
    createThread: async (data) => {
      try {
        const response = await api.post("/chat/create-thread", data);
        return response.data;
      } catch (error) {
        handleApiError(error, 'Failed to create chat thread');
        throw error;
      }
    },

    getThreads: async () => {
      try {
        const response = await api.get("/chat/threads");
        return response.data;
      } catch (error) {
        handleApiError(error, 'Failed to fetch chat threads');
        throw error;
      }
    },

    deleteThread: async (threadId) => {
      try {
        const response = await api.delete(`/chat/threads/${threadId}`);
        toast.success('Thread deleted successfully');
        return response.data;
      } catch (error) {
        handleApiError(error, 'Failed to delete thread');
        throw error;
      }
    },

    getMessages: async (threadId) => {
      try {
        const response = await api.get(`/chat/threads/${threadId}/messages`);
        return response.data;
      } catch (error) {
        handleApiError(error, 'Failed to fetch messages');
        throw error;
      }
    },

    sendMessage: async (threadId, message, isFirstMessage = false) => {
      try {
        const response = await api.post("/chat/send-message", {
          threadId,
          message,
          isFirstMessage
        });
        return response.data;
      } catch (error) {
        handleApiError(error, 'Failed to send message');
        throw error;
      }
    },

    renameThread: async (threadId, title) => {
      try {
        const response = await api.put(`/chat/threads/${threadId}/rename`, { title });
        return response.data;
      } catch (error) {
        handleApiError(error, 'Failed to rename thread');
        throw error;
      }
    },
  },
};

export default generalApi;
